import React from 'react';
import { X, Database, HardDrive, Zap, AlertCircle } from 'lucide-react';
import { useFileSearchStatusStore } from '../../store/useFileSearchStatus';
import type { InitTimingReport, InitTimingPerVolume } from '../../api/WebRTCSystem';

// 把毫秒数格式化成 "x.x s" 或 "xxx ms"
function fmtMs(ms: number): string {
  if (ms < 0) return '—';
  if (ms === 0) return '0';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

function fmtCount(n: number): string {
  return n.toLocaleString();
}

function strategyText(s: string): string {
  switch (s) {
    case 'all_loaded': return '全部从缓存加载';
    case 'all_full_scan': return '全部扫描';
    case 'mixed': return '混合(部分缓存+部分扫描)';
    case 'none': return '无可用磁盘';
    default: return s;
  }
}

function sourceText(s: string): string {
  switch (s) {
    case 'loaded': return '缓存加载';
    case 'full_scan': return '全盘扫描';
    case 'persist_disabled': return '持久化禁用';
    case 'smb_share': return 'SMB 共享';
    default: return s;
  }
}

interface StageBarProps {
  label: string;
  ms: number;
  totalMs: number;
  color: string;
}
const StageBar: React.FC<StageBarProps> = ({ label, ms, totalMs, color }) => {
  const pct = totalMs > 0 ? Math.max(1, Math.min(100, (ms / totalMs) * 100)) : 0;
  // ms < 0 表示 N/A (例如 contains async)，ms == 0 时也不画进度块
  const hasData = ms > 0;
  return (
    <div className="flex items-center gap-2 text-xs py-1">
      <div className="w-28 text-slate-400 truncate" title={label}>{label}</div>
      <div className="flex-1 h-3 bg-slate-800 rounded relative overflow-hidden">
        {hasData && (
          <div
            className={`h-full ${color} transition-all`}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
      <div className="w-20 text-right text-slate-300 tabular-nums">{fmtMs(ms)}</div>
    </div>
  );
};

interface InitTimingPanelProps {
  report: InitTimingReport;
  onClose: () => void;
}

const InitTimingPanel: React.FC<InitTimingPanelProps> = ({ report, onClose }) => {
  const totalMs = report.total_ms;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl bg-slate-900 border border-white/10 rounded-lg shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Database size={18} className="text-win-accent" />
            <h3 className="font-medium">本次初始化耗时报告</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* 总览 */}
          <div className="bg-slate-800/50 rounded p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">总耗时</span>
              <span className="text-2xl font-medium text-white tabular-nums">{fmtMs(totalMs)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">策略</span>
              <span className="text-sm text-slate-200 flex items-center gap-1">
                {report.strategy === 'all_loaded' && <Zap size={14} className="text-green-400" />}
                {report.strategy === 'all_full_scan' && <HardDrive size={14} className="text-amber-400" />}
                {report.strategy === 'mixed' && <Zap size={14} className="text-blue-400" />}
                {strategyText(report.strategy)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">磁盘统计</span>
              <span className="text-slate-300 tabular-nums">
                {report.volume_count_total} 个磁盘 ·
                <span className="text-green-400"> {report.volume_count_loaded_from_persist} 缓存</span> ·
                <span className="text-amber-400"> {report.volume_count_full_scan} 扫描</span>
                {report.volume_count_persist_disabled > 0 && (
                  <span className="text-red-400"> · {report.volume_count_persist_disabled} 禁用</span>
                )}
              </span>
            </div>
            {!report.success && report.error && (
              <div className="flex items-center gap-2 text-sm text-red-400">
                <AlertCircle size={14} /> 失败原因: {report.error}
              </div>
            )}
          </div>

          {/* 阶段瀑布 */}
          <div>
            <h4 className="text-sm text-slate-300 mb-2">阶段耗时</h4>
            <div className="bg-slate-800/30 rounded p-2">
              <StageBar label="打开磁盘"        ms={report.init_volumes_ms}        totalMs={totalMs} color="bg-slate-400" />
              <StageBar label="打开持久化"      ms={report.open_persistence_ms}    totalMs={totalMs} color="bg-blue-400" />
              <StageBar label="加载持久化数据"  ms={report.load_persistence_ms}    totalMs={totalMs} color="bg-green-500" />
              <StageBar label="USN 续传"        ms={report.replay_usn_ms}          totalMs={totalMs} color="bg-teal-400" />
              <StageBar label="扫盘 + 灌库"     ms={report.enumerate_volumes_ms}   totalMs={totalMs} color="bg-amber-500" />
              <StageBar label="前缀索引"        ms={report.prefix_index_ms}        totalMs={totalMs} color="bg-purple-400" />
              <StageBar
                label={`包含索引${report.contains_index_ms === -1 ? ' (异步)' : ''}`}
                ms={report.contains_index_ms === -1 ? 0 : report.contains_index_ms}
                totalMs={totalMs}
                color="bg-fuchsia-400"
              />
              <StageBar label="文件夹大小缓存"  ms={report.folder_stats_ms}        totalMs={totalMs} color="bg-indigo-400" />
              <StageBar label="首次写盘"        ms={report.bulk_persist_ms}        totalMs={totalMs} color="bg-cyan-400" />
              <StageBar label="启动 worker"     ms={report.start_workers_ms}       totalMs={totalMs} color="bg-slate-500" />
              <StageBar label="启动 USN 监控"   ms={report.start_usn_monitors_ms}  totalMs={totalMs} color="bg-slate-500" />
            </div>
          </div>

          {/* 每盘明细 */}
          {report.per_volume && report.per_volume.length > 0 && (
            <div>
              <h4 className="text-sm text-slate-300 mb-2">每个磁盘</h4>
              <div className="bg-slate-800/30 rounded overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-400">
                    <tr className="border-b border-white/5">
                      <th className="text-left px-2 py-2">盘符</th>
                      <th className="text-left px-2 py-2">来源</th>
                      <th className="text-right px-2 py-2">加载</th>
                      <th className="text-right px-2 py-2">续传</th>
                      <th className="text-right px-2 py-2">扫盘</th>
                      <th className="text-right px-2 py-2">首次写盘</th>
                      <th className="text-right px-2 py-2">文件数</th>
                      <th className="text-right px-2 py-2">目录数</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    {report.per_volume.map((pv: InitTimingPerVolume) => (
                      <tr key={pv.letter} className="border-b border-white/5 last:border-0">
                        <td className="px-2 py-1.5 font-medium">{pv.letter}:</td>
                        <td className="px-2 py-1.5 text-slate-400">
                          {sourceText(pv.source)}
                          {pv.replay_count > 0 && (
                            <span className="text-teal-400 ml-1">({pv.replay_count} USN)</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{pv.load_ms > 0 ? fmtMs(pv.load_ms) : '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{pv.replay_ms > 0 ? fmtMs(pv.replay_ms) : '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{pv.enumerate_ms > 0 ? fmtMs(pv.enumerate_ms) : '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{pv.bulk_persist_ms > 0 ? fmtMs(pv.bulk_persist_ms) : '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{pv.file_count > 0 ? fmtCount(pv.file_count) : '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{pv.dir_count > 0 ? fmtCount(pv.dir_count) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-white/10 bg-black/20 text-xs text-slate-500">
          开始: {new Date(report.start_unix_ms).toLocaleTimeString()} ·
          结束: {new Date(report.end_unix_ms).toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

// Wrapper: 从 store 读取状态决定是否渲染
const InitTimingPanelHost: React.FC = () => {
  const { initTiming, isInitTimingPanelOpen, closeInitTimingPanel } = useFileSearchStatusStore();
  if (!isInitTimingPanelOpen || !initTiming) return null;
  return <InitTimingPanel report={initTiming} onClose={closeInitTimingPanel} />;
};

export default InitTimingPanelHost;
